import axios from 'axios';

/**
 * Token storage + axios setup.
 * Uses localStorage so authenticated requests work across any cross-origin
 * setup (Cloudflare front, different subdomains, etc.) without relying on
 * SameSite cookie semantics.
 *
 * Tokens are still set as cookies by the backend as a fallback, but the
 * frontend now prefers Authorization: Bearer headers for every authed call.
 */

const ACCESS_KEY = 'verilock_access_token';
const REFRESH_KEY = 'verilock_refresh_token';

export const tokenStore = {
  getAccess: () => {
    try {
      return window.localStorage.getItem(ACCESS_KEY);
    } catch (e) {
      return null;
    }
  },
  getRefresh: () => {
    try {
      return window.localStorage.getItem(REFRESH_KEY);
    } catch (e) {
      return null;
    }
  },
  set: (access, refresh) => {
    try {
      if (access) window.localStorage.setItem(ACCESS_KEY, access);
      if (refresh) window.localStorage.setItem(REFRESH_KEY, refresh);
    } catch (e) {
      // privacy mode / disabled storage — silently degrade
    }
  },
  clear: () => {
    try {
      window.localStorage.removeItem(ACCESS_KEY);
      window.localStorage.removeItem(REFRESH_KEY);
    } catch (e) {
      // ignore
    }
  },
};

/**
 * Install axios interceptors:
 *   - request: attach Authorization: Bearer <access_token>
 *   - response 401: try /api/auth/refresh once, retry, otherwise clear and bubble
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_PATH = `${BACKEND_URL}/api/auth/refresh`;

let isRefreshing = false;
let pendingRequests = [];

const flushQueue = (newAccessToken) => {
  pendingRequests.forEach((cb) => cb(newAccessToken));
  pendingRequests = [];
};

export const installAxiosAuth = () => {
  // Cookies as fallback (won't hurt if blocked)
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const token = tokenStore.getAccess();
    if (token) {
      config.headers = config.headers || {};
      // Don't overwrite an explicit Authorization header
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      // Only attempt refresh on 401, once, and skip the refresh endpoint itself
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/set-password')
      ) {
        const refresh = tokenStore.getRefresh();
        if (!refresh) {
          tokenStore.clear();
          return Promise.reject(error);
        }
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue this request until the in-flight refresh resolves
          return new Promise((resolve, reject) => {
            pendingRequests.push((newAccessToken) => {
              if (!newAccessToken) {
                reject(error);
                return;
              }
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              resolve(axios(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const refreshResponse = await axios.post(
            REFRESH_PATH,
            { refresh_token: refresh },
            { withCredentials: true }
          );
          const newAccess = refreshResponse.data?.access_token;
          if (!newAccess) throw new Error('No access_token in refresh response');
          tokenStore.set(newAccess, null);
          flushQueue(newAccess);
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          return axios(originalRequest);
        } catch (refreshErr) {
          flushQueue(null);
          tokenStore.clear();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
};
